import { supabase } from "../lib/supabase";
import { fetchBookingStorageBlobViaEdge } from "./bookingStorageFileViaEdge";

async function loadOtherDocumentBlob(
  cleanPath: string,
  bookingId?: number
): Promise<Blob> {
  if (bookingId != null && Number.isFinite(bookingId)) {
    return fetchBookingStorageBlobViaEdge({
      bucket: "otherdocuments",
      objectPath: cleanPath,
      bookingId,
    });
  }
  const { data, error } = await supabase.storage
    .from("otherdocuments")
    .download(cleanPath);
  if (error || !data) {
    throw error ?? new Error("Could not download file");
  }
  return data;
}

/**
 * Sanitize filename to prevent special characters and spaces
 */
export const sanitizeFileName = (fileName: string): string => {
  if (!fileName) return `file_${Date.now()}`; // Handle undefined or null file names
  
  return fileName
    .replace(/\s+/g, "_") // Replace spaces with underscores
    .replace(/[^\w.-]/g, "") // Remove any non-alphanumeric characters except dots and hyphens
    .normalize("NFD") // Normalize accented characters
    .replace(/[\u0300-\u036f]/g, ""); // Remove diacritics
};

/**
 * Generate a file path for other documents using company ID as the root folder
 */
export const generateDocumentFilePath = (
  companyId: number | string,
  bookingId: number | string,
  fileName: string
): string => {
  // Sanitize the filename
  const sanitizedFileName = sanitizeFileName(fileName);
  
  // Create path with company_id as root
  return `${companyId}/${bookingId}/${Date.now()}_${sanitizedFileName}`;
};

/**
 * Upload an other document file using the company-based path structure
 */
export const uploadOtherDocument = async (
  bookingId: number | string,
  file: File,
  companyId: number | string = 0 // Make companyId optional with a default value
): Promise<{ error: unknown; path?: string } | null> => {
  try {
    if (!file) {
      console.error("No file provided for upload");
      return { error: "No file provided" };
    }
    
    console.log("uploadOtherDocument starting with:", {
      bookingId,
      companyId,
      fileName: file.name,
      fileSize: file.size,
      fileType: file.type
    });
    
    // Get company ID from booking if not provided
    const finalCompanyId = companyId || 0; // Use 0 as default company ID if not provided
    
    // Generate path with company ID as root
    const filePath = generateDocumentFilePath(finalCompanyId, bookingId, file.name);
    
    console.log("Generated file path:", filePath);
    
    // Upload to Supabase storage
    console.log("Uploading to bucket:", "otherdocuments");
    const { data, error } = await supabase.storage
      .from("otherdocuments")
      .upload(filePath, file, {
        cacheControl: "3600",
        upsert: false,
      });
    
    if (error) {
      console.error("Error uploading other document:", error);
      return { error };
    }
    
    console.log("Other document uploaded successfully:", data.path);
    return { error: null, path: data.path };
  } catch (error) {
    console.error("Error in uploadOtherDocument:", error);
    return { error };
  }
};

/**
 * Get public URL for an other document file
 */
export const getOtherDocumentPublicUrl = (
  path: string
): string | null => {
  try {
    if (!path || path.trim() === '') {
      return null;
    }
    
    // Clean the file path - remove any leading slash
    const cleanPath = path.trim().replace(/^\//, "");
    
    const { data } = supabase.storage
      .from("otherdocuments")
      .getPublicUrl(cleanPath);
    
    return data.publicUrl;
  } catch (error) {
    console.error("Error getting public URL:", error);
    return null;
  }
};

/**
 * Create a signed URL for other document file
 */
export const createOtherDocumentSignedUrl = async (
  path: string,
  expiresIn: number = 60,
  bookingId?: number
): Promise<string | null> => {
  void expiresIn;
  try {
    if (!path || path.trim() === '') {
      return null;
    }

    const cleanPath = path.trim().replace(/^\//, "");

    const blob = await loadOtherDocumentBlob(cleanPath, bookingId);
    return URL.createObjectURL(blob);
  } catch (error) {
    console.error("Error in createOtherDocumentSignedUrl:", error);
    return null;
  }
};

/**
 * Download an other document file
 */
export const downloadOtherDocument = async (
  path: string,
  filename: string,
  bookingId?: number
): Promise<void> => {
  try {
    if (!path || path.trim() === '') {
      throw new Error("Invalid file path provided");
    }
    
    // Clean the file path - remove any leading slash
    const cleanPath = path.trim().replace(/^\//, "");
    
    console.log("Downloading other document:", cleanPath);
    
    const fileExt = cleanPath.split(".").pop()?.toLowerCase() || "pdf";

    const blob = await loadOtherDocumentBlob(cleanPath, bookingId);

    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = downloadUrl;
    link.download = `${filename}.${fileExt}`;
    document.body.appendChild(link);
    link.click();
    
    // Clean up
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
    
    console.log("Download completed for:", filename);
  } catch (error) {
    console.error("Error downloading other document:", error);
    throw error;
  }
}; 